import React from 'react';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { ErrorOutline as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';

/**
 * Error Boundary component for catching and displaying JS errors
 * Prevents entire app from crashing when component errors occur
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });

        // Log the error to console or your error tracking service
        console.error('Error caught by boundary:', error, errorInfo);

        // If you have an error reporting service, you can send errors here
        // reportError({ error, errorInfo, componentStack: errorInfo.componentStack });
    }

    resetError = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    }

    render() {
        const { hasError, error, errorInfo } = this.state;
        const { children, fallback, showReset = true, showDetails = process.env.NODE_ENV === 'development' } = this.props;

        if (hasError) {
            // If a custom fallback is provided, use it
            if (fallback) {
                return typeof fallback === 'function'
                    ? fallback({ error, errorInfo, resetError: this.resetError })
                    : fallback;
            }

            // Default error UI
            return (
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        m: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderLeft: 4,
                        borderColor: 'error.main'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ErrorIcon color="error" sx={{ mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" color="error">
                            Something went wrong
                        </Typography>
                    </Box>

                    <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                        {error?.message || 'An unexpected error occurred'}
                    </Alert>

                    {showDetails && errorInfo?.componentStack && (
                        <Box
                            sx={{
                                maxHeight: 200,
                                overflow: 'auto',
                                width: '100%',
                                bgcolor: 'grey.100',
                                p: 2,
                                borderRadius: 1,
                                mb: 2,
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {errorInfo.componentStack}
                        </Box>
                    )}

                    {showReset && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={this.resetError}
                        >
                            Try Again
                        </Button>
                    )}
                </Paper>
            );
        }

        return children;
    }
}

export default ErrorBoundary; 